import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft, Sword, Target, Users, Trophy, Clock, Loader2,
  TrendingUp, BarChart3, Filter, Eye, RefreshCw, Crown, UserCheck, ListChecks, Flag, Ban, ExternalLink, Check, X
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const formatMesos = (value) => {
  if (!value && value !== 0) return "0";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return `${(num * 1000000).toLocaleString()} mesos`;
};

const CHART_COLORS = ["#f59e0b", "#3b82f6", "#10b981", "#8b5cf6", "#ef4444", "#06b6d4", "#f97316", "#ec4899"];

const STATUS_COLORS = {
  ACTIVE: "#22c55e", ASSIGNED: "#3b82f6", SUCCEEDED: "#eab308", CANCELLED: "#ef4444",
  INACTIVE: "#6b7280", ONGOING: "#f97316", AWAITING_BUYER_CONFIRM: "#a855f7",
  SETTLING_SPLITS: "#6366f1", SETTLED: "#6b7280"
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a2e] border border-amber-500/30 rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="text-amber-200 font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label, color, subValue }) => (
  <Card className={`bg-gradient-to-br from-${color}-900/30 to-${color}-950/30 border-${color}-500/30`}
    style={{ background: `linear-gradient(135deg, ${STATUS_COLORS[color] || '#f59e0b'}15, transparent)`, borderColor: `${STATUS_COLORS[color] || '#f59e0b'}40` }}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <Icon className="w-8 h-8" style={{ color: STATUS_COLORS[color] || '#f59e0b' }} />
        <div className="text-right">
          <p className="text-2xl font-bold text-amber-100">{value}</p>
          <p className="text-xs text-amber-300/60">{label}</p>
          {subValue && <p className="text-[10px] text-amber-300/40 mt-0.5">{subValue}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const LeaderboardTable = ({ data, columns, emptyText = "No data yet" }) => (
  <div className="space-y-1">
    {data.length === 0 ? (
      <p className="text-sm text-muted-foreground text-center py-4">{emptyText}</p>
    ) : data.map((row, i) => (
      <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-black/20 hover:bg-black/40 transition-colors">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-amber-500/30 text-amber-300' : 'bg-white/5 text-muted-foreground'}`}>
          {i + 1}
        </span>
        <span className="flex-1 text-sm text-amber-100 truncate">{row.username || "Unknown"}</span>
        {columns.map((col, j) => (
          <Badge key={j} variant="outline" className="text-xs tabular-nums" style={{ borderColor: `${col.color}60`, color: col.color }}>
            {row[col.key]} {col.suffix}
          </Badge>
        ))}
      </div>
    ))}
  </div>
);

const HuntersChanceAdminPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [expeditions, setExpeditions] = useState([]);
  const [bounties, setBounties] = useState([]);
  const [expTotal, setExpTotal] = useState(0);
  const [bountyTotal, setBountyTotal] = useState(0);
  const [statsPeriod, setStatsPeriod] = useState("all");
  const [expFilter, setExpFilter] = useState({ status: "" });
  const [bountyFilter, setBountyFilter] = useState({ status: "" });
  const [activeTab, setActiveTab] = useState("overview");
  const [hcReports, setHcReports] = useState([]);
  const [reportsFilter, setReportsFilter] = useState("pending");
  const [blockUsername, setBlockUsername] = useState("");
  const [bossImages, setBossImages] = useState({ bounty_bosses: [], expedition_bosses: [] });
  const [bossImagesLoading, setBossImagesLoading] = useState(false);
  const [editingBossImage, setEditingBossImage] = useState(null);
  const [editBossUrl, setEditBossUrl] = useState("");
  const [bossImageFilter, setBossImageFilter] = useState("expedition");

  useEffect(() => {
    if (user && !user.is_admin && !user.is_junior_admin) {
      toast.error("Admin access required");
      navigate("/hunters-chance");
    }
  }, [user, navigate]);

  const fetchAll = useCallback(async (silent = false) => {
    if (!token) return;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    if (!silent) setLoading(true);

    // Fire all requests independently - show data as it arrives
    axios.get(`${API_URL}/api/admin/stats/hunters-chance?period=${statsPeriod}`, auth)
      .then(res => { setStats(res.data); setLoading(false); }).catch(() => setLoading(false));
    axios.get(`${API_URL}/api/admin/stats/hunters-chance/detailed`, auth)
      .then(res => setDetailedStats(res.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/expeditions?${expFilter.status ? `status=${expFilter.status}` : ""}`, auth)
      .then(res => { setExpeditions(res.data.expeditions || []); setExpTotal(res.data.total || 0); }).catch(() => {});
    axios.get(`${API_URL}/api/admin/bounties?${bountyFilter.status ? `status=${bountyFilter.status}` : ""}`, auth)
      .then(res => { setBounties(res.data.bounties || []); setBountyTotal(res.data.total || 0); }).catch(() => {});
    axios.get(`${API_URL}/api/admin/hc-reports?status=${reportsFilter}`, auth)
      .then(res => setHcReports(res.data.reports || [])).catch(() => {});
  }, [token, statsPeriod, expFilter.status, bountyFilter.status, reportsFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshAll = () => fetchAll(true);

  const handleReportAction = async (reportId, action) => {
    try {
      await axios.put(`${API_URL}/api/admin/hc-reports/${reportId}?action=${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Report ${action}`);
      fetchAll(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update report");
    }
  };

  const handleBlockUser = async (username) => {
    if (!window.confirm(`Block user "${username}" site-wide? They won't be able to login.`)) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${username}/block`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`User ${username} blocked`);
      fetchAll(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to block user");
    }
  };

  const handleUnblockUser = async (username) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${username}/unblock`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`User ${username} unblocked`);
      fetchAll(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unblock user");
    }
  };

  const getExpStatusColor = (s) => ({
    ACTIVE: "bg-green-500/20 text-green-300", ONGOING: "bg-orange-500/20 text-orange-300",
    AWAITING_BUYER_CONFIRM: "bg-purple-500/20 text-purple-300", SETTLING_SPLITS: "bg-indigo-500/20 text-indigo-300",
    SUCCEEDED: "bg-yellow-500/20 text-yellow-300", SETTLED: "bg-gray-500/20 text-gray-300",
    CANCELLED: "bg-red-500/20 text-red-300", INACTIVE: "bg-gray-500/20 text-gray-400"
  }[s] || "bg-gray-500/20 text-gray-300");

  const fetchBossImages = async () => {
    setBossImagesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/boss-images`, { headers: { Authorization: `Bearer ${token}` } });
      setBossImages(res.data);
    } catch (err) {
      toast.error("Failed to load boss images");
    } finally {
      setBossImagesLoading(false);
    }
  };

  const handleUpdateBossImage = async (bossName, type, newUrl) => {
    try {
      await axios.put(`${API_URL}/api/admin/boss-images`, { boss_name: bossName, type, image_url: newUrl }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Image updated for ${bossName}`);
      setEditingBossImage(null);
      setEditBossUrl("");
      fetchBossImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleResetBossImage = async (bossName, type) => {
    try {
      await axios.delete(`${API_URL}/api/admin/boss-images`, { data: { boss_name: bossName, type }, headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Image reset for ${bossName}`);
      fetchBossImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset");
    }
  };

  const getBountyStatusColor = (s) => ({
    ACTIVE: "bg-green-500/20 text-green-300", ASSIGNED: "bg-blue-500/20 text-blue-300",
    SUCCEEDED: "bg-yellow-500/20 text-yellow-300", CANCELLED: "bg-red-500/20 text-red-300",
    INACTIVE: "bg-gray-500/20 text-gray-400"
  }[s] || "bg-gray-500/20 text-gray-300");

  if (!user?.is_admin && !user?.is_junior_admin) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="hc-admin-page">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b" style={{ background: "linear-gradient(180deg, #1a0f08 0%, #0a0604 100%)", borderColor: "rgba(217,119,6,0.3)" }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button data-testid="back-btn" variant="ghost" size="icon" onClick={() => navigate("/hunters-chance")} className="text-amber-300 hover:bg-amber-500/10">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-amber-200 flex items-center gap-2">
              <img src="/hc-logo.png" alt="HC" style={{ width: "40px", height: "23px", objectFit: "contain", filter: "drop-shadow(0 0 4px rgba(255,215,0,0.4))" }} /> Hunter's Chance Admin
            </h1>
            <p className="text-xs text-amber-300/60">Statistics, expeditions, and bounty management</p>
          </div>
          <Button data-testid="refresh-all-btn" variant="outline" size="sm" onClick={refreshAll} className="text-amber-300 border-amber-500/30 hover:bg-amber-500/10">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-black/40 flex-wrap h-auto gap-1 p-1" data-testid="admin-tabs">
            <TabsTrigger value="overview" className="data-[state=active]:bg-amber-500/30" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-cyan-500/30" data-testid="tab-activity">
              <TrendingUp className="w-4 h-4 mr-2" /> Activity
            </TabsTrigger>
            <TabsTrigger value="leaderboards" className="data-[state=active]:bg-yellow-500/30" data-testid="tab-leaderboards">
              <Crown className="w-4 h-4 mr-2" /> Leaderboards
            </TabsTrigger>
            <TabsTrigger value="expeditions" className="data-[state=active]:bg-blue-500/30" data-testid="tab-expeditions">
              <Users className="w-4 h-4 mr-2" /> Expeditions ({expTotal})
            </TabsTrigger>
            <TabsTrigger value="bounties" className="data-[state=active]:bg-red-500/30" data-testid="tab-bounties">
              <Target className="w-4 h-4 mr-2" /> Bounties ({bountyTotal})
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-orange-500/30" data-testid="tab-reports">
              <Flag className="w-4 h-4 mr-2" /> Reports {hcReports.filter(r => r.status === "pending").length > 0 && <Badge className="ml-1 bg-red-500 text-white text-[9px] px-1.5 py-0">{hcReports.filter(r => r.status === "pending").length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="boss-images" className="data-[state=active]:bg-emerald-500/30" data-testid="tab-boss-images">
              <Eye className="w-4 h-4 mr-2" /> Boss Images
            </TabsTrigger>
          </TabsList>

          {/* ==================== OVERVIEW TAB ==================== */}
          <TabsContent value="overview" data-testid="overview-content">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>
            ) : stats && (
              <div className="space-y-6">
                {/* Period Filter */}
                <div className="flex items-center gap-4">
                  <span className="text-sm text-amber-300/70">Period:</span>
                  <Select value={statsPeriod} onValueChange={setStatsPeriod}>
                    <SelectTrigger className="w-40 bg-black/40 border-amber-500/30" data-testid="period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <StatCard icon={Target} value={stats.bounties.total} label="Bounties" color="ACTIVE" />
                  <StatCard icon={Users} value={stats.expeditions.total} label="Expeditions" color="ASSIGNED" />
                  <StatCard icon={TrendingUp} value={`${stats.bounties.success_rate}%`} label="Bounty Success" color="SUCCEEDED" />
                  <StatCard icon={Trophy} value={`${stats.expeditions.success_rate}%`} label="Exp Success" color="SETTLING_SPLITS" />
                  <StatCard icon={UserCheck} value={detailedStats?.active_users_7d ?? "..."} label="Active (7d)" color="ONGOING" />
                  <StatCard icon={ListChecks} value={`${detailedStats?.active_wishes ?? 0} / ${detailedStats?.active_lfg ?? 0}`} label="Wishes / LFG" color="AWAITING_BUYER_CONFIRM" />
                </div>

                {/* Status Distributions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Bounty breakdown */}
                  <Card className="bg-card/50 border-red-500/20" data-testid="bounty-breakdown-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-red-300 flex items-center gap-2 text-sm">
                        <Target className="w-4 h-4" /> Bounty Status Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={detailedStats?.bounty_status_distribution || []}
                              dataKey="count"
                              nameKey="status"
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              paddingAngle={2}
                              label={({ status, count }) => `${status} (${count})`}
                            >
                              {(detailedStats?.bounty_status_distribution || []).map((entry, i) => (
                                <Cell key={i} fill={STATUS_COLORS[entry.status] || CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Expedition breakdown */}
                  <Card className="bg-card/50 border-blue-500/20" data-testid="expedition-breakdown-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-blue-300 flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4" /> Expedition Status Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={detailedStats?.expedition_status_distribution || []}
                              dataKey="count"
                              nameKey="status"
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              paddingAngle={2}
                              label={({ status, count }) => `${status} (${count})`}
                            >
                              {(detailedStats?.expedition_status_distribution || []).map((entry, i) => (
                                <Cell key={i} fill={STATUS_COLORS[entry.status] || CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Bosses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-card/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-amber-300 flex items-center gap-2 text-sm">
                        <Sword className="w-4 h-4" /> Top Bounty Bosses
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {stats.top_bounty_bosses?.length > 0 ? (
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.top_bounty_bosses.slice(0, 7)} layout="vertical" margin={{ left: 0, right: 10 }}>
                              <XAxis type="number" hide />
                              <YAxis type="category" dataKey="boss" tick={{ fill: '#d4a373', fontSize: 11 }} width={100} />
                              <Tooltip content={<CustomTooltip />} />
                              <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Bounties" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : <p className="text-sm text-muted-foreground py-4 text-center">No data</p>}
                    </CardContent>
                  </Card>
                  <Card className="bg-card/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-blue-300 flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4" /> Top Expedition Bosses
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {stats.top_expedition_bosses?.length > 0 ? (
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.top_expedition_bosses.slice(0, 7)} layout="vertical" margin={{ left: 0, right: 10 }}>
                              <XAxis type="number" hide />
                              <YAxis type="category" dataKey="boss" tick={{ fill: '#93c5fd', fontSize: 11 }} width={100} />
                              <Tooltip content={<CustomTooltip />} />
                              <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Expeditions" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : <p className="text-sm text-muted-foreground py-4 text-center">No data</p>}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ==================== ACTIVITY TAB ==================== */}
          <TabsContent value="activity" data-testid="activity-content">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
            ) : (
              <div className="space-y-6">
                {/* Daily Activity Chart */}
                <Card className="bg-card/50 border-cyan-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-cyan-300 flex items-center gap-2 text-sm">
                      <TrendingUp className="w-4 h-4" /> Daily Activity (Last 30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64" data-testid="daily-activity-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={detailedStats?.daily_activity || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} allowDecimals={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend wrapperStyle={{ fontSize: 12, color: '#9ca3af' }} />
                          <Line type="monotone" dataKey="bounties" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="Bounties" />
                          <Line type="monotone" dataKey="expeditions" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Expeditions" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Boss Performance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-card/50 border-amber-500/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-amber-300 flex items-center gap-2 text-sm">
                        <Target className="w-4 h-4" /> Bounty Boss Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-64">
                        <div className="space-y-2 pr-3">
                          {(detailedStats?.boss_bounty_performance || []).map((b, i) => (
                            <div key={i} className="flex items-center gap-3 bg-black/20 rounded-lg px-3 py-2">
                              <span className="text-sm text-amber-200 flex-1 truncate">{b.boss}</span>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-muted-foreground">{b.total} total</span>
                                <span className="text-green-400">{b.succeeded} won</span>
                                <Badge variant="outline" className="text-xs tabular-nums" style={{ borderColor: b.success_rate > 50 ? '#22c55e60' : '#ef444460', color: b.success_rate > 50 ? '#22c55e' : '#ef4444' }}>
                                  {b.success_rate}%
                                </Badge>
                              </div>
                            </div>
                          ))}
                          {(!detailedStats?.boss_bounty_performance?.length) && (
                            <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  <Card className="bg-card/50 border-blue-500/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-blue-300 flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4" /> Expedition Boss Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-64">
                        <div className="space-y-2 pr-3">
                          {(detailedStats?.boss_expedition_performance || []).map((b, i) => (
                            <div key={i} className="flex items-center gap-3 bg-black/20 rounded-lg px-3 py-2">
                              <span className="text-sm text-blue-200 flex-1 truncate">{b.boss}</span>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-muted-foreground">{b.total} total</span>
                                <span className="text-green-400">{b.succeeded} won</span>
                                <Badge variant="outline" className="text-xs tabular-nums" style={{ borderColor: b.success_rate > 50 ? '#22c55e60' : '#ef444460', color: b.success_rate > 50 ? '#22c55e' : '#ef4444' }}>
                                  {b.success_rate}%
                                </Badge>
                              </div>
                            </div>
                          ))}
                          {(!detailedStats?.boss_expedition_performance?.length) && (
                            <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ==================== LEADERBOARDS TAB ==================== */}
          <TabsContent value="leaderboards" data-testid="leaderboards-content">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-yellow-400" /></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Top Hunters */}
                <Card className="bg-card/50 border-green-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-green-300 flex items-center gap-2 text-sm">
                      <Sword className="w-4 h-4" /> Top Hunters
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <LeaderboardTable
                      data={detailedStats?.top_hunters || []}
                      columns={[
                        { key: "completed_bounties", suffix: "hunts", color: "#f59e0b" },
                        { key: "expedition_participations", suffix: "exps", color: "#3b82f6" }
                      ]}
                      emptyText="No hunters yet"
                    />
                  </CardContent>
                </Card>

                {/* Top Clients */}
                <Card className="bg-card/50 border-purple-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-purple-300 flex items-center gap-2 text-sm">
                      <Crown className="w-4 h-4" /> Top Clients
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <LeaderboardTable
                      data={detailedStats?.top_clients || []}
                      columns={[
                        { key: "bounties_posted", suffix: "posted", color: "#a855f7" }
                      ]}
                      emptyText="No clients yet"
                    />
                  </CardContent>
                </Card>

                {/* Top Expedition Leaders */}
                <Card className="bg-card/50 border-orange-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-orange-300 flex items-center gap-2 text-sm">
                      <Trophy className="w-4 h-4" /> Top Expedition Leaders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <LeaderboardTable
                      data={detailedStats?.top_leaders || []}
                      columns={[
                        { key: "expeditions_led", suffix: "led", color: "#f97316" }
                      ]}
                      emptyText="No leaders yet"
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* ==================== EXPEDITIONS TAB ==================== */}
          <TabsContent value="expeditions" data-testid="expeditions-content">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <Select value={expFilter.status} onValueChange={(v) => setExpFilter({ status: v })}>
                  <SelectTrigger className="w-48 bg-black/40 border-blue-500/30" data-testid="exp-status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">All Statuses</SelectItem>
                    <SelectItem value="ACTIVE">Recruiting</SelectItem>
                    <SelectItem value="ONGOING">In Progress</SelectItem>
                    <SelectItem value="AWAITING_BUYER_CONFIRM">Awaiting Buyer</SelectItem>
                    <SelectItem value="SETTLING_SPLITS">Settling Splits</SelectItem>
                    <SelectItem value="SUCCEEDED,SETTLED">Succeeded</SelectItem>
                    <SelectItem value="CANCELLED,INACTIVE">Cancelled/Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={refreshAll} className="text-blue-300 border-blue-500/30">
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>
              <ScrollArea className="h-[600px]">
                <div className="space-y-2">
                  {expeditions.map((exp) => (
                    <Card key={exp.id} className="bg-card/30 border-blue-500/20 hover:border-blue-500/40 transition-colors cursor-pointer" onClick={() => navigate(`/hunters-chance/expedition/${exp.id}`)} data-testid={`exp-row-${exp.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {exp.boss_image && <img src={exp.boss_image} alt={exp.boss_name} className="w-10 h-10 rounded-lg" />}
                            <div>
                              <p className="font-medium text-blue-200">{exp.boss_name}</p>
                              <p className="text-xs text-muted-foreground">by {exp.leader_username}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-xs text-muted-foreground">
                              <p>{exp.member_count || 0}/{exp.total_slots} members</p>
                              <p>{new Date(exp.created_at).toLocaleDateString()}</p>
                            </div>
                            <Badge className={getExpStatusColor(exp.status)}>{exp.status}</Badge>
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {expeditions.length === 0 && <p className="text-center text-muted-foreground py-8">No expeditions found</p>}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* ==================== BOUNTIES TAB ==================== */}
          <TabsContent value="bounties" data-testid="bounties-content">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <Select value={bountyFilter.status} onValueChange={(v) => setBountyFilter({ status: v })}>
                  <SelectTrigger className="w-48 bg-black/40 border-red-500/30" data-testid="bounty-status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">All Statuses</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="ASSIGNED">Assigned</SelectItem>
                    <SelectItem value="SUCCEEDED">Succeeded</SelectItem>
                    <SelectItem value="CANCELLED,INACTIVE">Cancelled/Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={refreshAll} className="text-red-300 border-red-500/30">
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>
              <ScrollArea className="h-[600px]">
                <div className="space-y-2">
                  {bounties.map((bounty) => (
                    <Card key={bounty.id} className="bg-card/30 border-red-500/20 hover:border-red-500/40 transition-colors cursor-pointer" onClick={() => navigate(`/hunters-chance/bounty/${bounty.id}`)} data-testid={`bounty-row-${bounty.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Target className="w-8 h-8 text-amber-400" />
                            <div>
                              <p className="font-medium text-red-200">{bounty.boss_name}</p>
                              <p className="text-xs text-muted-foreground">by {bounty.client_info?.username || "Unknown"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-xs">
                              <p className="text-yellow-400">{formatMesos(bounty.reward)}</p>
                              <p className="text-muted-foreground">{new Date(bounty.created_at).toLocaleDateString()}</p>
                            </div>
                            <Badge className={getBountyStatusColor(bounty.status)}>{bounty.status}</Badge>
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {bounties.length === 0 && <p className="text-center text-muted-foreground py-8">No bounties found</p>}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* ==================== REPORTS TAB ==================== */}
          <TabsContent value="reports" data-testid="reports-content">
            <div className="space-y-4">
              {/* Filter + Block User */}
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={reportsFilter} onValueChange={(v) => setReportsFilter(v)}>
                  <SelectTrigger className="w-40 bg-black/40 border-gray-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <div className="ml-auto flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Username to block..."
                    value={blockUsername}
                    onChange={(e) => setBlockUsername(e.target.value)}
                    className="bg-black/40 border border-gray-600 rounded-md px-3 py-2 text-sm text-white w-48"
                    data-testid="block-username-input"
                  />
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => { if (blockUsername.trim()) handleBlockUser(blockUsername.trim()); setBlockUsername(""); }} data-testid="block-user-btn">
                    <Ban className="w-4 h-4 mr-1" /> Block
                  </Button>
                  <Button size="sm" variant="outline" className="border-green-600 text-green-400 hover:bg-green-600/10" onClick={() => { if (blockUsername.trim()) handleUnblockUser(blockUsername.trim()); setBlockUsername(""); }} data-testid="unblock-user-btn">
                    Unblock
                  </Button>
                </div>
              </div>

              {/* Reports List */}
              <ScrollArea className="h-[60vh]">
                <div className="space-y-3">
                  {hcReports.map(report => (
                    <Card key={report.id} className="bg-black/30 border-orange-500/20">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Flag className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`text-[10px] ${report.target_type === "bounty" ? "bg-red-500/20 text-red-300 border-red-500/30" : "bg-blue-500/20 text-blue-300 border-blue-500/30"}`}>
                                {report.target_type.toUpperCase()}
                              </Badge>
                              <span className="text-sm text-amber-200 font-medium">{report.target_name}</span>
                              <span className="text-xs text-gray-500">by {report.target_owner_username || "?"}</span>
                              <Badge className={`text-[10px] ml-auto ${report.status === "pending" ? "bg-orange-500/20 text-orange-300" : report.status === "resolved" ? "bg-green-500/20 text-green-300" : "bg-gray-500/20 text-gray-300"}`}>
                                {report.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-300">{report.reason}</p>
                            <div className="flex items-center gap-2 text-[10px] text-gray-500">
                              <span>Reported by: {report.reporter_username}</span>
                              <span>{new Date(report.created_at).toLocaleString("en-GB", { timeZone: "UTC", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} UTC</span>
                              {report.resolved_by && <span>Handled by: {report.resolved_by}</span>}
                            </div>
                            {/* Participants with ban buttons */}
                            {report.participants && report.participants.length > 0 && (
                              <div className="flex items-center gap-1 flex-wrap mt-1">
                                <span className="text-[10px] text-gray-500 mr-1">Participants:</span>
                                {report.participants.map((p, idx) => (
                                  <div key={idx} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-gray-800 border border-gray-700">
                                    <span className="text-gray-300">{p.username}</span>
                                    <span className="text-gray-500">({p.role})</span>
                                    <button onClick={() => handleBlockUser(p.username)} className="text-red-400 hover:text-red-300 ml-0.5" title={`Block ${p.username}`}>
                                      <Ban className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" variant="ghost" className="text-blue-400 hover:bg-blue-600/10 h-8 px-2" onClick={() => navigate(`/hunters-chance/${report.target_type === "bounty" ? "bounty" : "expedition"}/${report.target_id}`)} data-testid={`view-report-${report.id}`} title="View reported item">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                            {report.target_owner_username && (
                              <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-600/10 h-8 px-2" onClick={() => handleBlockUser(report.target_owner_username)} data-testid={`block-owner-${report.id}`} title={`Block organizer: ${report.target_owner_username}`}>
                                <Ban className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {report.status === "pending" && (
                              <>
                                <Button size="sm" variant="ghost" className="text-green-400 hover:bg-green-600/10 h-8 px-2" onClick={() => handleReportAction(report.id, "resolved")} data-testid={`resolve-report-${report.id}`} title="Mark as resolved">
                                  <Check className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-gray-400 hover:bg-gray-600/10 h-8 px-2" onClick={() => handleReportAction(report.id, "dismissed")} data-testid={`dismiss-report-${report.id}`} title="Dismiss report">
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {hcReports.length === 0 && <p className="text-center text-muted-foreground py-8">No reports found</p>}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* ==================== BOSS IMAGES TAB ==================== */}
          <TabsContent value="boss-images" data-testid="boss-images-content">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-amber-200">Boss Image URLs</h2>
                <div className="flex gap-2">
                  <Select value={bossImageFilter} onValueChange={setBossImageFilter}>
                    <SelectTrigger className="w-[150px] h-8 text-xs bg-black/40 border-amber-500/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expedition">Expedition Bosses</SelectItem>
                      <SelectItem value="bounty">Bounty/Tracker Bosses</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={fetchBossImages} data-testid="refresh-boss-images">
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                  </Button>
                </div>
              </div>

              {bossImagesLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>
              ) : (
                <ScrollArea className="h-[600px]">
                  <div className="space-y-2">
                    {(bossImageFilter === "expedition" ? bossImages.expedition_bosses : bossImages.bounty_bosses).map((boss) => (
                      <div key={`${boss.type}-${boss.name}`} className="flex items-center gap-3 p-3 rounded-lg bg-black/20 border border-amber-500/10 hover:border-amber-500/30 transition-colors" data-testid={`boss-image-row-${boss.name}`}>
                        <img
                          src={boss.current_url}
                          alt={boss.name}
                          className="w-12 h-12 object-contain rounded border border-amber-500/20 bg-black/40"
                          onError={(e) => { e.target.src = "https://legends.ml/static/images/lib/monster/6130101.png"; }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-amber-100">{boss.name}</p>
                          {editingBossImage === `${boss.type}-${boss.name}` ? (
                            <div className="flex gap-2 mt-1">
                              <input
                                type="text"
                                value={editBossUrl}
                                onChange={(e) => setEditBossUrl(e.target.value)}
                                className="flex-1 text-xs px-2 py-1 rounded bg-black/60 border border-amber-500/30 text-white focus:outline-none focus:border-amber-400"
                                placeholder="Enter new image URL"
                                data-testid={`boss-image-url-input-${boss.name}`}
                              />
                              <Button size="sm" className="h-6 px-2 text-[10px] bg-green-600/30 hover:bg-green-600/50 text-green-300" onClick={() => handleUpdateBossImage(boss.name, boss.type, editBossUrl)}>
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button size="sm" className="h-6 px-2 text-[10px] bg-red-600/30 hover:bg-red-600/50 text-red-300" onClick={() => { setEditingBossImage(null); setEditBossUrl(""); }}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <p className="text-[10px] text-amber-300/50 truncate">{boss.current_url}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {boss.is_custom && (
                            <Badge className="bg-emerald-500/20 text-emerald-300 text-[9px] px-1.5 py-0">Custom</Badge>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-[10px] text-amber-300 hover:bg-amber-500/10"
                            onClick={() => {
                              setEditingBossImage(`${boss.type}-${boss.name}`);
                              setEditBossUrl(boss.current_url);
                            }}
                            data-testid={`edit-boss-image-${boss.name}`}
                          >
                            Edit
                          </Button>
                          {boss.is_custom && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-[10px] text-red-300 hover:bg-red-500/10"
                              onClick={() => handleResetBossImage(boss.name, boss.type)}
                              data-testid={`reset-boss-image-${boss.name}`}
                            >
                              Reset
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {(bossImageFilter === "expedition" ? bossImages.expedition_bosses : bossImages.bounty_bosses).length === 0 && (
                      <p className="text-center text-amber-300/50 py-8">Click "Refresh" to load boss images</p>
                    )}
                  </div>
                </ScrollArea>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HuntersChanceAdminPage;
